import AlertSlackOfError from "../../monitoring/AlertSlackOfError";
import { convertToUSD, getFinalPrice } from "./getFinalPrice";
import { isSidePatchActive, SIDE_PATCH_COLOR_COMPONENT_IDS } from "./sidePatchState";

/** Placement suffixes from variant displayNames (e.g. "Gator - Ribs"); strip for cart / order copy. Order matters: longer suffixes first. */
const PATTERN_PLACEMENT_SUFFIX_RES = [/ - multi ribs$/i, / - ribs$/i, / - sides$/i];

export function stripPatternPlacementSuffix(value) {
  if (typeof value !== "string") return value;
  let out = value.trim();
  for (const re of PATTERN_PLACEMENT_SUFFIX_RES) {
    out = out.replace(re, "").trim();
  }
  return out;
}

/** Insert Top Logo / Side Logos into cart properties (call right after Thread Color). */
function appendLogoProperties(cartSubmissionObj, productsState, itemsState) {
  const topLogoItem = itemsState.activeObjs?.["patch-material-rear"];
  cartSubmissionObj.items[0].properties["Top Logo"] =
    topLogoItem?._id === "no-top-logo" ? "None" : stripPatternPlacementSuffix(topLogoItem?.displayName ?? "None");

  const sideLogoItemId = itemsState.activeObjs?.["side-logo"]?._id;
  if (sideLogoItemId === "yes-side-logos" && !productsState?.activeObj?.shopify?.["omit-side-patches"]) {
    cartSubmissionObj.items[0].properties["Side Logos"] = "YES";
  }
}

export const getShoppingCartData = (productsState, componentsState, itemsState, quantity, finalNotes, recipientName) => {
  // Validate input states
  if (!productsState?.activeObj || !componentsState?.array || !itemsState?.activeObjs) {
    console.error("Invalid state in getShoppingCartData:", {
      productsState,
      componentsState,
      itemsState,
    });
    AlertSlackOfError(
      "getShoppingCartData",
      `Invalid state: products=${!!productsState?.activeObj}, components=${!!componentsState?.array}, items=${!!itemsState?.activeObjs}`,
    );
  }

  // const variantId = productsState?.activeObj.shopify.shopify_variant_id;

  // create object that will be submitted to cart
  let cartSubmissionObj = {
    items: [
      {
        // id: variantId, // NOTE: this is not needed here since we're generating a product at checkout time and will use that id to add-to-cart
        quantity,
        properties: {
          Brand: `${productsState?.activeObj.shopify.brand}`,
          Model: `${productsState?.activeObj.shopify.model}`,
          Year: `${productsState?.activeObj.shopify.year}`,
          ...(productsState?.activeObj.shopify.template ? { Template: `${productsState?.activeObj.shopify.template}` } : {}),
          ...(itemsState?.activeObjs["comfort-foam"]?._id == "yes-comfort-foam"
            ? { "Comfort Foam": `${itemsState.activeObjs["comfort-foam"].displayName}` }
            : {}),
          ...(itemsState?.activeObjs["double-side-wall"]?._id == "yes-double-side-wall"
            ? { "Double Side Wall": `${itemsState.activeObjs["double-side-wall"].displayName}` }
            : {}),
          ...(itemsState?.activeObjs["tall-seat-cover"]?._id == "yes-tall-seat-cover"
            ? { "Tall Seat Cover": `${itemsState.activeObjs["tall-seat-cover"].displayName}` }
            : {}),
          ...(productsState?.activeObj.shopify.strap ? { "Strap Length": `${productsState?.activeObj.shopify.strap}"` } : {}),
          ...(productsState?.activeObj.shopify["gas-cap"] ? { "Gas Cap": "Included" } : {}),
        },
      },
    ],
  };

  /**
   * add the 'properties' data to the obj so the full description of the product is present in the cart
   */

  // leave these out since we add them up above ^
  const componentsToSkip = ["comfort-foam", "double-side-wall", "tall-seat-cover", "patch-material-rear", "side-logo"];

  const sidePatchActive = isSidePatchActive({ itemsState, productsState });

  componentsState.array.forEach((componentObj) => {
    try {
      if (componentsToSkip.includes(componentObj._id)) return;
      // Side patch colors must not appear without a fully active side patch
      if (SIDE_PATCH_COLOR_COMPONENT_IDS.includes(componentObj._id) && !sidePatchActive) return;
      if (!componentObj.excluded) {
        let activeItem = itemsState.activeObjs[componentObj._id];

        let key = componentObj.displayName;
        let value = activeItem.displayName;

        // NOTE: this logic is not needed since we're only submitting 1 custom generated product to the cart
        // add the variant id to the cartSubmissionObj
        // if (activeItem?.shopify_variant_id && activeItem.price > 0) {
        //   cartSubmissionObj.items.push({
        //     id: activeItem?.shopify_variant_id,
        //     quantity,
        //   });
        // }

        // CUSTOM CODE: create visual seperator in cart
        // if (componentObj._id === "custom-patch-front") {
        //   cartSubmissionObj.items[0].properties["------------------------------------"] = "-";
        // }

        // hide custom patches if they are not set
        if (componentObj._id?.includes("custom-patch") && activeItem._id.includes("no-")) return;

        // make the value be the text_input if the customText items are active
        if (componentObj._id?.includes("custom-patch") && (activeItem._id === "customText-front-patch" || activeItem._id === "customText-side-patches")) {
          cartSubmissionObj.items[0].properties[key] = `"${activeItem.inputs.text_input}"`;
          // add additional font info
          cartSubmissionObj.items[0].properties[`${key} Font`] = activeItem.font_array[activeItem.inputs.active_font_index].displayName;
          return;
        }

        // update the cartSubmissionObj with the custom image
        if (componentObj._id?.includes("custom-patch") && activeItem._id.includes("customImage")) {
          if (activeItem.uploaded_logo_base64.length > 3) {
            cartSubmissionObj.items[0].properties[key] = "Custom Image";
            // this is used as temporary storage for the custom image so we don't have to find these items again
            cartSubmissionObj.items[0].properties["Custom Image URL"] = activeItem.uploaded_logo_base64;
            cartSubmissionObj.customPatchFront = componentObj._id;
            cartSubmissionObj.tempCustomUploadImageInputs = {
              _id: activeItem._id,
              inputs: { ...activeItem.inputs },
            };
          }
          // image has already been uploaded and is in the form of a src url
          else {
            cartSubmissionObj.items[0].properties["Custom Image URL"] = activeItem.uploaded_logo_src;
          }
          return;
        }

        cartSubmissionObj.items[0].properties[key] = stripPatternPlacementSuffix(value);

        // Place logos directly under Thread Color (before Custom Patch fields)
        if (componentObj._id === "stitching-material") {
          appendLogoProperties(cartSubmissionObj, productsState, itemsState);
        }
      }
    } catch (error) {
      console.log("Error setting cart property:", componentObj, error);
      AlertSlackOfError("!!!!!!!!! getShoppingCartData !!!!!!!!!!!", `${componentObj._id} - ${error}}`);
    }
  });

  if (productsState?.activeObj.shopify["hole-punch-front"]) {
    cartSubmissionObj.items[0].properties["Hole Punch"] = "Front";
  } else if (productsState?.activeObj.shopify["hole-punch-back"]) {
    cartSubmissionObj.items[0].properties["Hole Punch"] = "Back";
  }

  if (productsState?.activeObj.shopify.cordura) {
    const cordura = productsState.activeObj.shopify.cordura;
    cartSubmissionObj.items[0].properties["Cordura"] = cordura.charAt(0).toUpperCase() + cordura.slice(1);
  }

  if (finalNotes.length > 0) {
    cartSubmissionObj.items[0].properties["Final Order Notes"] = finalNotes;
  }

  if (recipientName.length > 0) {
    cartSubmissionObj.items[0].properties["Recipient's Name"] = recipientName;
  }

  // Dealer only: _msrp = inflate (price + items) by base msrp/price ratio.
  // Requires the msrp metafield — without it we cannot derive the discount ratio.
  const dealerShopify = productsState?.activeObj?.shopify;
  if (dealerShopify?.isDealer) {
    if (dealerShopify.msrp) {
      const returnMsrpTotal = true;
      const totalMsrp = getFinalPrice(productsState, componentsState, itemsState, returnMsrpTotal);
      const totalMsrpUsd = convertToUSD(totalMsrp);
      console.log("[getShoppingCartData] dealer _msrp", {
        totalMsrpPresentment: totalMsrp,
        totalMsrpUsd,
        basePrice: dealerShopify.price,
        baseMsrp: dealerShopify.msrp,
      });
      cartSubmissionObj.items[0].properties._msrp = totalMsrpUsd;
    } else {
      console.warn("[getShoppingCartData] dealer session but no msrp metafield; skipping _msrp");
    }
  }

  return [cartSubmissionObj];
};
